import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Check, Ship, Users, MapPin, X, BusFront } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "./Field";
import { api, apiErrorMessage } from "@/lib/api";

const today = () => new Date().toISOString().split("T")[0];
const empty = { name: "", email: "", phone: "", date: today(), time: "", party_size: 2, notes: "" };

export default function VanLiftModal({ open, onClose, minimumSpend = 30 }) {
  const [form, setForm] = useState(empty);
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadTimes = useCallback(async (date) => {
    try {
      const { data } = await api.get(`/ferry/times?date=${date}`);
      setTimes(data.times);
      setForm((f) => ({ ...f, time: data.times[0] || "" }));
    } catch { setTimes([]); }
  }, []);

  useEffect(() => { if (open) loadTimes(form.date); /* eslint-disable-next-line */ }, [form.date, open]);

  if (!open) return null;

  const close = () => { setForm(empty); setDone(false); onClose?.(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) return toast.error("Pick the ferry you're arriving on.");
    setLoading(true);
    try {
      await api.post("/bookings/van", { ...form, party_size: Number(form.party_size) });
      setDone(true);
      toast.success("Van request sent — we'll confirm your lift!");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
      <div
        data-testid="van-lift-modal"
        className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg w-full max-w-lg p-6 md:p-8 max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <p className="overline text-secondary flex items-center gap-2"><BusFront size={14} /> The Haze Van</p>
            <h3 className="mt-2 font-head font-black tracking-tight leading-[0.95] text-2xl md:text-3xl">Got the ferry? Grab a lift</h3>
          </div>
          <button onClick={close} data-testid="van-lift-close" className="p-1.5 border-2 border-foreground rounded-md shrink-0"><X size={16} /></button>
        </div>

        {done ? (
          <div className="text-center py-8" data-testid="van-booking-success">
            <Check className="mx-auto text-secondary" size={40} />
            <h3 className="mt-4 font-head font-extrabold text-2xl">Van request received!</h3>
            <p className="mt-2 text-foreground/70 max-w-sm mx-auto">
              We'll confirm your lift from the {form.time} ferry on {form.date}. See you at the jetty!
            </p>
            <button
              onClick={() => { setForm(empty); setDone(false); }}
              data-testid="van-booking-reset"
              className="mt-6 font-head font-bold bg-foreground text-background px-6 py-3 rounded-full hover:bg-primary hover:text-foreground transition-colors"
            >
              Book another lift
            </button>
          </div>
        ) : (
          <>
            <div className="mt-2 mb-5 grid grid-cols-3 gap-2 font-mono-plex text-[11px] text-foreground/70">
              {[
                { icon: Ship, t: "Pick your ferry" },
                { icon: Users, t: "Tell us numbers" },
                { icon: MapPin, t: "We meet you" },
              ].map((s) => (
                <div key={s.t} className="flex flex-col items-center gap-1.5 text-center bg-muted border-2 border-foreground/15 rounded-lg py-3">
                  <s.icon size={16} className="text-secondary" /> {s.t}
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="van-booking-form">
              <Field label="Your name"><TextInput data-testid="van-name" required value={form.name} onChange={set("name")} placeholder="Jane Cook" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Date"><TextInput data-testid="van-date" type="date" min={today()} required value={form.date} onChange={set("date")} /></Field>
                <Field label="Arriving ferry" hint="Diamond Harbour arrivals">
                  <SelectInput data-testid="van-time" value={form.time} onChange={set("time")} required>
                    {times.map((t) => <option key={t} value={t}>{t}</option>)}
                  </SelectInput>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Number of people"><TextInput data-testid="van-party" type="number" min={1} max={12} required value={form.party_size} onChange={set("party_size")} /></Field>
                <Field label="Phone"><TextInput data-testid="van-phone" required value={form.phone} onChange={set("phone")} placeholder="021 123 4567" /></Field>
              </div>
              <Field label="Email"><TextInput data-testid="van-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
              <Field label="Notes"><TextArea data-testid="van-notes" value={form.notes} onChange={set("notes")} placeholder="Luggage, pram, dog…" /></Field>
              <button
                type="submit" disabled={loading} data-testid="van-submit"
                className="w-full font-head font-bold text-lg bg-primary text-foreground border-2 border-foreground rounded-full py-4 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
              >
                {loading ? "Sending…" : "Request van lift"}
              </button>
              <p className="text-center font-mono-plex text-[11px] text-foreground/50">${minimumSpend} minimum spend applies to van bookings</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
